import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Toast, ToastContainer } from '@/components/Toast';

interface ToastContextType {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  dismissToast: (id: string) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 5000, // Default 5 seconds
    };

    setToasts((prev) => [...prev, newToast]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showSuccess = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'success', title, message });
    },
    [showToast]
  );

  const showError = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'error', title, message, duration: 10000 }); // Errors stay longer
    },
    [showToast]
  );

  const showWarning = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'warning', title, message });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'info', title, message });
    },
    [showToast]
  );

  return (
    <ToastContext.Provider
      value={{
        toasts,
        showToast,
        dismissToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
      }}
    >
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

// Utility functions for common error scenarios
export function useErrorHandler() {
  const { showError, showWarning } = useToast();

  const handleStorageError = useCallback(
    (error: any) => {
      if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
        showError(
          'Permission Denied',
          'Please grant file system access to continue. You may need to allow permissions in your browser settings.'
        );
      } else if (error.name === 'QuotaExceededError') {
        showError(
          'Storage Full',
          'Your browser storage is full. Please clear some space and try again.'
        );
      } else if (error.name === 'NotFoundError') {
        showWarning(
          'File Not Found',
          'The requested file could not be found. It may have been moved or deleted.'
        );
      } else if (error.message?.includes('network')) {
        showError('Network Error', 'Please check your internet connection and try again.');
      } else {
        showError(
          'Operation Failed',
          error.message || 'An unexpected error occurred. Please try again.'
        );
      }
    },
    [showError, showWarning]
  );

  const handleApiError = useCallback(
    (error: any) => {
      if (error.status === 401) {
        showError('Authentication Required', 'Please sign in to continue.');
      } else if (error.status === 403) {
        showError('Access Denied', "You don't have permission to perform this action.");
      } else if (error.status === 404) {
        showWarning('Not Found', 'The requested resource could not be found.');
      } else if (error.status === 429) {
        showWarning('Too Many Requests', 'Please slow down and try again in a moment.');
      } else if (error.status >= 500) {
        showError('Server Error', 'Something went wrong on our end. Please try again later.');
      } else {
        showError('Request Failed', error.message || 'The request could not be completed.');
      }
    },
    [showError, showWarning]
  );

  return {
    handleStorageError,
    handleApiError,
  };
}
